/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import { useFormik } from "formik";
import { ButtonTypes } from "../../utils/consts";
import { useState } from "react";
import Upload from "../../components/Shared/Upload";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getChapters } from "../../api/Chapters/GetAllChapters";
import {
  CourseUpdatePayload,
  editCourse,
} from "../../api/Chapters/EditCourseContent";
import { queryClient } from "../../main";
import { toast } from "react-toastify";
import QuizXIcon from "../../components/Icons/QuizXIcon";
import { DeleteVideos } from "../../api/Courses/DeleteVideos";
import Spinner from "../../components/Shared/Spinner";
import { PatchLecture } from "../../api/Lecture/PatchLecture";
import { PatchVideo } from "../../api/Videos/PatchVideo";
import { PatchResource } from "../../api/Resources/PatchResource";
import { PatchQuiz } from "../../api/Quiz/PatchQuiz";
import DemoToggle from "../../components/Dashboard/Courses/DemoToggle";

const EditCourseContent = () => {
  const { id } = useParams();
  const [deletedVideoIds, setDeletedVideoIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const courseId = id || "";
  const { data: ChaptersResponse, isLoading } = useQuery({
    queryFn: () => getChapters(courseId),
    queryKey: ["chapter", courseId],
    staleTime: 0,
    refetchInterval: 0,
  });
  const chaptersRaw = ChaptersResponse?.data || [];
  const courseAllowDemo = chaptersRaw[0]?.course?.allowDemo || false;
  const getLectureRaw = (mi: number, li: number) =>
    chaptersRaw[mi]?.lectures?.[li];
  const invalidateChapters = [["chapter", courseId]];
  const { mutate: deleteVideosMutate } = useMutation({
    mutationFn: (payload: { ids: string[] }) => DeleteVideos(payload),
    onError: (error) => {
      toast.error("Error deleting videos");
      console.error(error);
    },
  });

  const { mutate: editCourseMutate } = useMutation({
    mutationFn: (payload: CourseUpdatePayload) => editCourse(courseId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      formik.setSubmitting(false);
      toast.success("Course Edited successfully");
    },
    onError: (error) => {
      toast.error("Error editing course");
      console.error(error);
      formik.setSubmitting(false);
    },
  });

  //@ts-ignore
  const transformToInitialValues = (chapters) => {
    return chapters.map(
      (module: {
        id: string;
        name: string;
        description: string;
        lectures: {
          id: string;
          title: string;
          videos: { id: string; name: string; url: string }[];
          resources: { id: string; title: string; url: string }[];
          quizizz: { id: string; title: string }[];
          order: number;
        }[];
        order: number;
      }) => ({
        id: module.id,
        moduleName: module.name,
        moduleDescription: module.description,
        lectures: module.lectures.map(
          (lecture: {
            id: string;
            title: string;
            order: number;
            videos: { id: string; name: string; url: string }[];
            resources: { id: string; title: string; url: string }[];
            quizizz: { id: string; title: string }[];
          }) => ({
            id: lecture.id,
            title: lecture.title,
            order: lecture.order,
            videos: lecture.videos.map(
              (video: { id: string; name: string; url: string }) => ({
                id: video.id,
                name: video.name,
                url: video.url,
              })
            ),
            resources: lecture.resources.map(
              (resource: { id: string; title: string; url: string }) => ({
                id: resource.id,
                title: resource.title,
                url: resource.url,
              })
            ),
            quizizz: lecture.quizizz || [],
          })
        ),
        moduleOrder: module.order,
      })
    );
  };

  const initialValues = transformToInitialValues(ChaptersResponse?.data || []);

  const formik = useFormik({
    initialValues: { modules: initialValues },
    enableReinitialize: true,
    onSubmit: (values) => {
      const formattedPayload = values.modules.map(
        (module: {
          id: string;
          moduleName: string;
          moduleDescription: string;
          lectures: {
            id: string;
            title: string;
            videos: { id: string; name: string; url: string }[];
            resources: { id: string; title: string; url: string }[];
            quizizz: { id: string; title: string }[];
            order: number;
          }[];
          moduleOrder: number;
        }) => ({
          id: module.id,
          name: module.moduleName,
          description: module.moduleDescription,
          lectures: module.lectures.map(
            (lecture: {
              id: string;
              title: string;
              order: number;
              videos: { id: string; name: string; url: string }[];
              resources: { id: string; title: string; url: string }[];
              quizizz: { id: string; title: string }[];
            }) => ({
              id: lecture.id,
              title: lecture.title,
              order: lecture.order,
              videos: lecture.videos,
              resources: lecture.resources,
              quizizz: lecture.quizizz.map(
                (quiz: { id: string; title: string }) => ({
                  id: quiz.id,
                  title: quiz.title || "", // Handling potentially empty titles
                })
              ),
            })
          ),
          order: module.moduleOrder,
        })
      );

      // Delete the videos first
      if (deletedVideoIds.length > 0) {
        deleteVideosMutate(
          { ids: deletedVideoIds },
          {
            onSuccess: () => {
              // Now edit the course
              editCourseMutate(formattedPayload);
            },
          }
        );
      } else {
        // No videos to delete, directly edit the course
        editCourseMutate(formattedPayload);
      }
    },
  });

  const handleVideoUrlUpload = (
    url: string,
    moduleIndex: number,
    lectureIndex: number,
    videoIndex: number
  ) => {
    const fieldName = `modules[${moduleIndex}].lectures[${lectureIndex}].videos[${videoIndex}].url`;
    formik.setFieldValue(fieldName, url);
  };
  const handleResourceUrlUpload = (
    url: string,
    moduleIndex: number,
    lectureIndex: number,
    resourceIndex: number
  ) => {
    const fieldName = `modules[${moduleIndex}].lectures[${lectureIndex}].resources[${resourceIndex}].url`;
    formik.setFieldValue(fieldName, url);
  };

  const handleAddVideo = (moduleIndex: number, lectureIndex: number) => {
    const videos =
      formik.values.modules[moduleIndex].lectures[lectureIndex].videos;
    const newVideo = { id: `${Math.random()}`, name: "", url: "" };
    const updatedVideos = [...videos, newVideo];
    const fieldName = `modules[${moduleIndex}].lectures[${lectureIndex}].videos`;
    formik.setFieldValue(fieldName, updatedVideos);
  };

  const handleAddLecture = (moduleIndex: number) => {
    const lectures = formik.values.modules[moduleIndex].lectures;
    const newLecture = {
      id: `${Math.random()}`,
      title: "",
      videos: [],
      resources: [],
      quizizz: [],
    };
    const updatedLectures = [...lectures, newLecture];
    const fieldName = `modules[${moduleIndex}].lectures`;
    formik.setFieldValue(fieldName, updatedLectures);
  };

  const handleDeleteLecture = (moduleIndex: number, lectureIndex: number) => {
    const lectures = formik.values.modules[moduleIndex].lectures.filter(
      (_: any, index: any) => index !== lectureIndex
    );
    const fieldName = `modules[${moduleIndex}].lectures`;
    formik.setFieldValue(fieldName, lectures);
  };

  const handleDeleteVideo = (
    moduleIndex: number,
    lectureIndex: number,
    videoIndex: number
  ) => {
    const videoId =
      formik.values.modules[moduleIndex].lectures[lectureIndex].videos[
        videoIndex
      ].id;
    setDeletedVideoIds((prev) => [...prev, videoId]);
    const videos = formik.values.modules[moduleIndex].lectures[
      lectureIndex
    ].videos.filter((_: any, index: any) => index !== videoIndex);
    const fieldName = `modules[${moduleIndex}].lectures[${lectureIndex}].videos`;
    formik.setFieldValue(fieldName, videos);
  };

  const handleDeleteResource = (
    moduleIndex: number,
    lectureIndex: number,
    resourceIndex: number
  ) => {
    const resources = formik.values.modules[moduleIndex].lectures[
      lectureIndex
    ].resources.filter((_: any, index: any) => index !== resourceIndex);
    const fieldName = `modules[${moduleIndex}].lectures[${lectureIndex}].resources`;
    formik.setFieldValue(fieldName, resources);
  };

  const handleOrderChange = (id: string, oldOrder: number, order: number) => {
    const sameModuleOrder = formik.values.modules.findIndex(
      (mod: any) => mod.id != id && mod.moduleOrder == order
    );
    if (sameModuleOrder != -1) {
      formik.setFieldValue(
        "modules",
        formik.values.modules.map((mod: any, idx: number) =>
          sameModuleOrder == idx
            ? {
                ...mod,
                moduleOrder: oldOrder,
              }
            : mod
        )
      );
    }
  };

  const handleLectureOrderChange = (
    chapterId: string,
    lectureId: string,
    oldOrder: number,
    newOrder: number
  ) => {
    const sameModule = formik.values.modules.find(
      (mod: any) => mod.id == chapterId
    );

    if (sameModule) {
      // Find the lecture that currently has the new order (if any)
      const conflictingLecture = sameModule.lectures.find(
        (lect: any) => lect.id !== lectureId && lect.order === newOrder
      );

      // Update the target lecture to new order
      const updatedModules = formik.values.modules.map((mod: any) =>
        mod.id === chapterId
          ? {
              ...mod,
              lectures: mod.lectures.map((lect: any) =>
                lect.id === lectureId ? { ...lect, order: newOrder } : lect
              ),
            }
          : mod
      );

      // If there's a conflicting lecture, swap its order with the old order
      if (conflictingLecture) {
        const finalModules = updatedModules.map((mod: any) =>
          mod.id === chapterId
            ? {
                ...mod,
                lectures: mod.lectures.map((lect: any) =>
                  lect.id === conflictingLecture.id
                    ? { ...lect, order: oldOrder }
                    : lect
                ),
              }
            : mod
        );
        formik.setFieldValue("modules", finalModules);
      } else {
        formik.setFieldValue("modules", updatedModules);
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} className="pt-5 pb-20">
      <div className="flex justify-between mb-6">
        <h1 className="text-burntBlack font-bold text-[32px] mt-5">
          Edit lectures
        </h1>
        <Button
          onClick={() => navigate(-1)}
          className="!h-11 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
          variant="primary"
          type={ButtonTypes.BUTTON}
        >
          Get Back
        </Button>
      </div>
      <div className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5">
        <div className="flex flex-col gap-2 mb-2">
          <h1 className="font-bold text-2xl text-primary">Course Content</h1>
          <p className="text-lightGray font-medium max-w-2xl">
            Add your modules here. Every module averages 30-60 minutes, divided
            into short video lessons of 2-15 minutes (almost).
          </p>
        </div>
        {isLoading ? (
          <div
            style={{ minHeight: "50svh" }}
            className="flex justify-center align-items-center"
          >
            <Spinner txt="Loading Course Content..." />
          </div>
        ) : (
          formik.values.modules.map((module: any, moduleIndex: any) => (
            <div key={moduleIndex} className="w-full">
              <div className="w-full">
                <h1 className="font-bold text-2xl">Module {moduleIndex + 1}</h1>
                <div className="space-y-7 pl-4 pr-6 py-5 mt-2 rounded-lg bg-[#F5F6F8]">
                  <div className="flex gap-5 w-full">
                    <Input
                      value={formik.values.modules[moduleIndex].moduleOrder}
                      onChange={(e: any) => {
                        handleOrderChange(
                          formik.values.modules[moduleIndex].id,
                          formik.values.modules[moduleIndex].moduleOrder,
                          Number(e.target.value)
                        );
                        formik.handleChange(e);
                      }}
                      onBlur={formik.handleBlur}
                      name={`modules[${moduleIndex}].moduleOrder`}
                      className="text-lg font-semibold w-full"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Module Order"
                      placeholder="Enter module order"
                      type="number"
                    />
                    <Input
                      value={formik.values.modules[moduleIndex].moduleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name={`modules[${moduleIndex}].moduleName`}
                      className="text-lg font-semibold w-full"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Module Name"
                      placeholder="Enter module name"
                      type="text"
                    />
                    <Input
                      value={
                        formik.values.modules[moduleIndex].moduleDescription
                      }
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name={`modules[${moduleIndex}].moduleDescription`}
                      className="text-lg font-semibold w-full"
                      inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                      title="Description"
                      placeholder="Enter module description"
                      type="text"
                    />
                  </div>
                  {module.lectures.map((lecture: any, lectureIndex: any) => (
                    <div key={lectureIndex}>
                      <div className="flex justify-between items-center gap-5 w-full">
                        <Input
                          value={
                            formik.values.modules[moduleIndex].lectures[
                              lectureIndex
                            ].order
                          }
                          onChange={(e: any) => {
                            handleLectureOrderChange(
                              formik.values.modules[moduleIndex].id,
                              formik.values.modules[moduleIndex].lectures[
                                lectureIndex
                              ].id,
                              formik.values.modules[moduleIndex].lectures[
                                lectureIndex
                              ].order,
                              Number(e.target.value)
                            );
                            formik.handleChange(e);
                          }}
                          onBlur={formik.handleBlur}
                          name={`modules[${moduleIndex}].lectures[${lectureIndex}].order`}
                          className="text-lg font-semibold"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                          title="Lecture Order"
                          placeholder="Enter lecture order"
                          type="number"
                        />
                        <Input
                          value={
                            formik.values.modules[moduleIndex].lectures[
                              lectureIndex
                            ].title
                          }
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name={`modules[${moduleIndex}].lectures[${lectureIndex}].title`}
                          className="text-lg font-semibold w-full"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                          title={`Lecture ${lectureIndex + 1} Name`}
                          placeholder="Enter lecture name"
                          type="text"
                        />
                        {getLectureRaw(moduleIndex, lectureIndex)?.id && (
                          <DemoToggle
                            key={`lec-demo-${getLectureRaw(moduleIndex, lectureIndex)?.id}`}
                            label="Lecture Demo"
                            isDemo={!!getLectureRaw(moduleIndex, lectureIndex)?.isDemo}
                            disabled={!courseAllowDemo}
                            disabledReason="Enable Allow Demo on the course first"
                            onPatch={(next) =>
                              PatchLecture(getLectureRaw(moduleIndex, lectureIndex)!.id, {
                                isDemo: next,
                              })
                            }
                            invalidateKeys={invalidateChapters}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteLecture(moduleIndex, lectureIndex)
                          }
                          className="text-red-500 min-h-7 min-w-7 rounded-full bg-white border flex items-center justify-center shrink-0"
                        >
                          <QuizXIcon fill="rgba(239 68 68)" />
                        </button>
                      </div>
                      {lecture.videos.map((_video: any, videoIndex: any) => (
                        <div
                          key={videoIndex}
                          className="flex flex-col gap-2 w-full mb-3"
                        >
                          <div className="flex justify-between items-center gap-5 w-full">
                          <Input
                            value={
                              formik.values.modules[moduleIndex].lectures[
                                lectureIndex
                              ].videos[videoIndex].name
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            name={`modules[${moduleIndex}].lectures[${lectureIndex}].videos[${videoIndex}].name`}
                            className="text-lg font-semibold w-full grow"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            title={`Video ${videoIndex + 1} Title`}
                            placeholder="Enter video title"
                            type="text"
                          />
                          <Upload
                            className="w-full md:w-[180px] md:max-w-[180px] md:shrink-0"
                            acceptedFiles={{
                              "video/mp4": [],
                              "video/webm": [],
                              "video/ogg": [],
                            }}
                            isVideo
                            onFileUploaded={(url) =>
                              handleVideoUrlUpload(
                                url,
                                moduleIndex,
                                lectureIndex,
                                videoIndex
                              )
                            }
                            folder="courses_videos"
                          >
                            <Input
                              upload={true}
                              value={
                                formik.values.modules[moduleIndex].lectures[
                                  lectureIndex
                                ].videos[videoIndex].url
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              name={`modules[${moduleIndex}].lectures[${lectureIndex}].videos[${videoIndex}].url`}
                              className="text-lg font-semibold w-full"
                              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                              title={`Video URL`}
                              placeholder="Upload or enter video URL"
                              type="text"
                            />
                          </Upload>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteVideo(
                                moduleIndex,
                                lectureIndex,
                                videoIndex
                              )
                            }
                            className="text-red-500 min-h-7 min-w-7 rounded-full bg-white border flex items-center justify-center shrink-0"
                          >
                            <QuizXIcon fill="rgba(239 68 68)" />
                          </button>
                          </div>
                          {getLectureRaw(moduleIndex, lectureIndex)?.videos?.[videoIndex]?.id && (
                            <div className="flex justify-end pr-12">
                              <DemoToggle
                                key={`vid-demo-${getLectureRaw(moduleIndex, lectureIndex)?.videos?.[videoIndex]?.id}`}
                                isDemo={!!getLectureRaw(moduleIndex, lectureIndex)?.videos?.[videoIndex]?.isDemo}
                                disabled={!getLectureRaw(moduleIndex, lectureIndex)?.isDemo}
                                disabledReason="Enable Lecture Demo first"
                                onPatch={(next) =>
                                  PatchVideo(
                                    getLectureRaw(moduleIndex, lectureIndex)!.videos[videoIndex].id,
                                    { isDemo: next }
                                  )
                                }
                                invalidateKeys={invalidateChapters}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      <Button
                        type={ButtonTypes.BUTTON}
                        onClick={() =>
                          handleAddVideo(moduleIndex, lectureIndex)
                        }
                        disabled={formik.isSubmitting}
                        variant="primary"
                        className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold my-5"
                      >
                        Add Video
                      </Button>

                      {lecture.resources.map(
                        (_resource: any, resourceIndex: any) => (
                          <div
                            key={resourceIndex}
                            className="flex flex-col gap-2 w-full mb-3"
                          >
                            <div className="flex justify-between items-center gap-5 w-full">
                            <Input
                              value={
                                formik.values.modules[moduleIndex].lectures[
                                  lectureIndex
                                ].resources[resourceIndex].title
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              name={`modules[${moduleIndex}].lectures[${lectureIndex}].resources[${resourceIndex}].title`}
                              className="text-lg font-semibold w-full grow"
                              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                              title={`Resource ${resourceIndex + 1} Title`}
                              placeholder="Enter resource title"
                              type="text"
                            />
                            <Upload
                              className="w-full md:w-[180px] md:max-w-[180px] md:shrink-0 /* resource-url-bigger */"
                              acceptedFiles={{ pdf: [] }}
                              folder="courses_resources"
                              onFileUploaded={(url) =>
                                handleResourceUrlUpload(
                                  url,
                                  moduleIndex,
                                  lectureIndex,
                                  resourceIndex
                                )
                              }
                            >
                              <Input
                                upload={true}
                                value={
                                  formik.values.modules[moduleIndex].lectures[
                                    lectureIndex
                                  ].resources[resourceIndex].url
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name={`modules[${moduleIndex}].lectures[${lectureIndex}].resources[${resourceIndex}].url`}
                                className="text-lg font-semibold w-full"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                title={`Resource URL`}
                                placeholder="Upload or enter resource URL"
                                type="text"
                              />
                            </Upload>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteResource(
                                  moduleIndex,
                                  lectureIndex,
                                  resourceIndex
                                )
                              }
                              className="text-red-500 min-h-7 min-w-7 rounded-full bg-white border flex items-center justify-center shrink-0"
                            >
                              <QuizXIcon fill="rgba(239 68 68)" />
                            </button>
                            </div>
                            {getLectureRaw(moduleIndex, lectureIndex)?.resources?.[resourceIndex]?.id && (
                              <div className="flex justify-end pr-12">
                                <DemoToggle
                                  key={`res-demo-${getLectureRaw(moduleIndex, lectureIndex)?.resources?.[resourceIndex]?.id}`}
                                  isDemo={!!getLectureRaw(moduleIndex, lectureIndex)?.resources?.[resourceIndex]?.isDemo}
                                  disabled={!getLectureRaw(moduleIndex, lectureIndex)?.isDemo}
                                  disabledReason="Enable Lecture Demo first"
                                  onPatch={(next) =>
                                    PatchResource(
                                      getLectureRaw(moduleIndex, lectureIndex)!.resources[resourceIndex].id,
                                      { isDemo: next }
                                    )
                                  }
                                  invalidateKeys={invalidateChapters}
                                />
                              </div>
                            )}
                          </div>
                        )
                      )}
                      <Button
                        type={ButtonTypes.BUTTON}
                        disabled={formik.isSubmitting}
                        variant="primary"
                        className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
                      >
                        Add Resource
                      </Button>
                      {(getLectureRaw(moduleIndex, lectureIndex)?.quizizz?.length ?? 0) > 0 && (
                        <div className="mt-5 space-y-2">
                          <h2 className="text-sm font-bold text-burntBlack">Quizzes</h2>
                          {getLectureRaw(moduleIndex, lectureIndex)!.quizizz!.map((q) => (
                            <div key={q.id} className="flex justify-between items-center bg-white border border-black/15 rounded px-3 py-2">
                              <span className="text-sm">{q.title || "Untitled quiz"}</span>
                              <DemoToggle
                                key={`quiz-demo-${q.id}`}
                                isDemo={!!q.isDemo}
                                disabled={!getLectureRaw(moduleIndex, lectureIndex)?.isDemo}
                                disabledReason="Enable Lecture Demo first"
                                onPatch={(next) => PatchQuiz(q.id, { isDemo: next })}
                                invalidateKeys={invalidateChapters}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <Button
                    type={ButtonTypes.BUTTON}
                    onClick={() => handleAddLecture(moduleIndex)}
                    disabled={formik.isSubmitting}
                    variant="primary"
                    className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-10 w-40 px-1 text-lg font-bold"
                  >
                    Add Lecture
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}

        <div className="pt-6 flex gap-9">
          <Button
            type={ButtonTypes.SUBMIT}
            disabled={formik.isSubmitting}
            variant="primary"
            className="!h-10 w-40 px-1 text-lg font-bold"
          >
            {formik.isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Loading...</span>
              </div>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EditCourseContent;
